'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import { getAllTaxonomies } from '@/apis/TaxonomyAPIs';

import { capitalizeFirstLetter, removeLeadingTrailingSlashes } from '@/utils/formattingStrings';
import { validateBackslash } from '@/utils/urls';

import { BreadcrumbItemType } from './BreadCrumbComponent';

/* -------------------------------------------------------------
 * Normalize any API response into an array
 * ------------------------------------------------------------- */
function normalizeToArray(data: any): any[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === 'object') return Object.values(data);
  return [];
}

/* -------------------------------------------------------------
 * MAIN HOOK
 * ------------------------------------------------------------- */
export const useBreadcrumbItems = (title: string): BreadcrumbItemType[] => {
  const pathname = usePathname();
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItemType[]>([]);

  useEffect(() => {
    if (!pathname) return;

    const run = async () => {
      const parts = removeLeadingTrailingSlashes(pathname).split('/').filter(Boolean);

      const output: BreadcrumbItemType[] = [];
      let fullPath = '';

      /* ---------------------------------------------------------
       * LOAD TAXONOMIES
       * --------------------------------------------------------- */
      const taxonomyResponse = await getAllTaxonomies();
      const allTaxonomies = normalizeToArray(taxonomyResponse);

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        const isLast = i === parts.length - 1;

        fullPath += `/${part}`;

        let label = capitalizeFirstLetter(part.replace(/-/g, ' '));

        /* ---------------------------------------------------------
         * 1. FIRST SEGMENT → TAXONOMY
         * --------------------------------------------------------- */
        if (i === 0) {
          const taxonomy = allTaxonomies.find((t: any) => t.slug === part);
          if (taxonomy?.name) {
            label = taxonomy.name;
          }
        }

        /* ---------------------------------------------------------
         * 2. SECOND SEGMENT → FORCE TITLE
         * --------------------------------------------------------- */
        if (i === 1 && title) {
          label = title;
        }

        /* ---------------------------------------------------------
         * 3. THIRD+ SEGMENTS → RAW SLUG
         * --------------------------------------------------------- */
        if (i >= 2) {
          label = capitalizeFirstLetter(part);
        }

        output.push({
          label,
          href: isLast ? undefined : validateBackslash(fullPath),
          isCurrentPage: isLast,
        });
      }

      setBreadcrumbs(output);
    };

    run();
  }, [pathname, title]);

  return breadcrumbs;
};
